// What a role may OPEN. The decision is not made here: it is read from
// `app/access-model.ts`, the single source the sidebar reads too, so the menu
// and the routes cannot come to disagree. On a mismatch, send the reader to
// their own role's home.
// per docs/shape-ops-access-model.md §The model (owner ruling, 2026-09-05)
//
// This file used to ask the MENU whether a role could open an address, which
// answered a different question with the same field. It now asks `mayOpen`,
// which defaults to the menu and widens only where the caiet says so — so the
// `home` clause that used to sit in `decideRoute` is a row of the table with its
// citation beside it, and so is `expiring`, the link the stock view draws for
// every role.
import type { Role, SessionUser } from '@simsm/shared-types';
import { accessTargetFor, mayOpen, type NavContext } from '../access-model';
import { homePathFor } from '../role-home';
import { hasHeldSession } from '../session-presence';

export type RouteDecision =
  | { verdict: 'allow' }
  | { verdict: 'redirect'; to: string }
  | { verdict: 'sign-in' };

// The path segment a role's screens live under, read off the one map that
// already answers it for the role picker, the dev login and /login-success.
export function roleSegmentOf(role: Role): string {
  return homePathFor(role).split('/')[1] ?? '';
}

// Where a reader with no session goes. Never a role home: they hold no role.
// The two answers are the ones api-client's 401 interceptor already
// distinguishes — a session that lapsed while someone was working is worth
// saying out loud, an address opened without ever signing in is not an expiry
// and telling that reader otherwise would be a sentence we know to be false.
export function signInPath(): string {
  return hasHeldSession() ? '/auth/session-expired' : '/';
}

export function decideRoute(
  pathname: string,
  session: SessionUser | null,
  context?: NavContext,
): RouteDecision {
  if (session === null) return { verdict: 'sign-in' };

  const role = session.activeRole;
  const home = homePathFor(role);
  const segment = roleSegmentOf(role);

  // The URL must not assert a role the session does not hold. The shell builds
  // every link off this segment, so an operator left at /system-admin/reports
  // would browse their own menu under an address that claims otherwise.
  if (pathname.split('/')[1] !== segment) return { verdict: 'redirect', to: home };

  // No owner, no entry: a route with no rule is forbidden to everyone rather
  // than quietly public.
  const target = accessTargetFor(pathname, segment);
  if (target === null) return { verdict: 'redirect', to: home };

  // caiet UC10-02 + arbitration D1: Stocuri IMSP depends on the KIND of the
  // active Registrar, which the role code alone cannot express. The context is
  // the one the sidebar reads (useNavContext), and it fails closed when nothing
  // answers the question.
  return mayOpen(target, role, context) ? { verdict: 'allow' } : { verdict: 'redirect', to: home };
}
